import { type PropsWithChildren, type ReactNode } from 'react'
import { isNotDefined, Logger, type WithWorkflowId } from '@terros/common'
import { useSelectedWorkflow, useToastError } from '@hooks'
import { type LoadedWorkflow, useWorkflow, useWorkflowContextValue, WorkflowContext } from '@front/model'
import { NotReady } from '@components'

const logger = new Logger('WorkflowProvider')

export const WorkflowProvider = ({ children }: PropsWithChildren): ReactNode => {
  const { workflowId, isLoading, error } = useSelectedWorkflow()
  useToastError(error)
  if (isLoading) {
    return (
      <div className='flex min-h-screen items-center'>
        <NotReady isLoading />
      </div>
    )
  }
  if (isNotDefined(workflowId)) return children
  return <LoadWorkflowProvider workflowId={workflowId}>{children}</LoadWorkflowProvider>
}

const LoadWorkflowProvider = ({ children, workflowId }: PropsWithChildren<WithWorkflowId>): ReactNode => {
  const { data: workflow, ...state } = useWorkflow(workflowId)
  useToastError(state.error)
  if (state.isLoading) {
    return (
      <div className='flex min-h-screen items-center'>
        <NotReady isLoading />
      </div>
    )
  }
  if (!workflow) return children

  return (
    <LoadedWorkflowProvider data={workflow} {...state}>
      {children}
    </LoadedWorkflowProvider>
  )
}

const LoadedWorkflowProvider = ({ children, ...props }: PropsWithChildren<LoadedWorkflow>): ReactNode => {
  const contextValue = useWorkflowContextValue(props)
  return <WorkflowContext.Provider value={contextValue}>{children}</WorkflowContext.Provider>
}
